import {
  Flex,
  HStack,
  Popover,
  PopoverTrigger,
  Spinner,
  Text,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  VStack,
  IconButton,
  Icon,
  Link,
  Portal,
  Box,
} from '@chakra-ui/react';
import { useUserInfo } from '@transport-insights/uikit';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import InformationSourcePopup from '@src/modules/performance/components/InformationSourcePopup';
import { useIsFetching } from 'react-query';
import { FiLink2 } from 'react-icons/fi';

function LinksPopup({ links }) {
  const { roles, isLoggedIn } = useUserInfo();
  // Filter links based on user roles
  // A link will be shown if it has no userRoles specified, or if the user has at least one of the roles specified in userRoles, or if userRoles includes 'All' and the user is logged in
  const filteredLinks = links?.filter(
    (link) => !link.userRoles || link.userRoles.length === 0 || link.userRoles.some((role) => roles?.includes(role)) || (link.userRoles.includes('All') && isLoggedIn),
  );

  if (!filteredLinks || filteredLinks.length === 0) {
    return null;
  }

  return (
    <Popover
      trigger="hover"
      isLazy
      zIndex="999"
    >
      <PopoverTrigger>
        <IconButton
          variant="ghost"
          colorScheme="gray"
          icon={<Icon as={FiLink2} boxSize="16px" />}
          aria-label="links"
          size="xs"
          zIndex="10!important"
          _focus={{ boxShadow: 'none' }}
          className="hidden-print"
        />
      </PopoverTrigger>
      <Portal>
        <Box position="relative" zIndex={9999}>
          <PopoverContent width="fit-content">
            <PopoverArrow />
            <PopoverBody>
              <VStack align="start">
                {filteredLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.url}
                    target="_blank"
                    fontSize="sm"
                  >
                    {link.label}
                  </Link>
                ))}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Box>
      </Portal>
    </Popover>
  );
}

export default function ReportSection({
  title, subtitle = null, lockYear = null, quarter = null, quarterFormat = 'month', informationSource = null, links = null, isLoading, contentProps = {}, children, ...rest
}) {
  const isFetching = useIsFetching();
  const isLoaded = !isLoading && isFetching === 0;
  return (
    <Flex
      background="white"
      boxShadow="base"
      borderRadius="md"
      className="report-section-container"
      flexDir="column"
      align="center"
      flexGrow={1}
      px={2}
      pt={2}
      pb={4}
      {...rest}
      sx={{
        '@media print': {
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#fff',
          boxShadow: 'none',
        },
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        mb={3}
        width="100%"
        px={2}
        className="report-section-header"
      >
        <VStack align="start" spacing={0}>
          <HStack spacing={0} mb={0}>
            <Text
              whiteSpace="pre-line"
              textAlign="left"
              fontWeight="bold"
              color="gray.700"
              fontSize={13}
              lineHeight={1.1}
              mr={1}
            >
              {title}
            </Text>
            {informationSource && (
              <InformationSourcePopup
                title={informationSource.title}
                content={informationSource.content}
                source={informationSource.source}
              />
            )}
            {links && (
            <LinksPopup
              links={links}
            />
            )}
          </HStack>
          {subtitle && (
            <Text
              whiteSpace="pre-line"
              textAlign="left"
              color="gray.500"
              fontSize={11}
              lineHeight={1.1}
            >
              {subtitle}
            </Text>
          )}
        </VStack>
        {lockYear && (
        <LockYearBadge lockYear={lockYear} quarter={quarter} quarterFormat={quarterFormat} />
        )}
      </Flex>
      <Flex
        flexDir="column"
        flexGrow={1}
        justify="center"
        align="center"
        width="100%"
        px={2}
        {...contentProps}
      >
        {!isLoaded ? <Spinner color="brand.orange.500" size="lg" my={10} /> : children}
      </Flex>
    </Flex>
  );
}
